import React from 'react';
import { FormantsChart } from '../chart/FormantsChart';
import { allFormants, rhoticFormants } from '../../phonemes/formants';
import { IpaType } from '../../phonemes/ipa';
import { speakSegment } from '../speakSegment';
import { speakPhonemes } from '../../speak/speakPhonemes';

type LanguageType = 'US' | 'W-US';

/** show formant chart for a particular language/accent, optionally with rhotics */
export function SpecificFormants(props: { which: LanguageType, rhotics?: boolean }) {
  const formants = getFormants(props.which, props.rhotics);
  const diphthongs = getDiphthongProp(props.which, props.rhotics);
  const ignoreDiphthongFormants = props.which === 'US'; // snap US to formants of pieces

  function speak(ipa: string) {
    if (ipa.endsWith('ɹ')) {
      // if ignore, fake a rhotic so that it doesn't use the built-in sound
      const toSay = ignoreDiphthongFormants ? `ˈ${ipa[0]}ɹ9`
        : ipa[0] !== "ˈ" ? `ˈ${ipa}`  // make sure it's stressed
          : ipa;
      speakPhonemes(toSay);
    } else {
      speakSegment(ipa);
    }
  }

  return <FormantsChart width={400} height={250}
    formants={formants} diphthongs={diphthongs} ignoreDiphthongFormants={ignoreDiphthongFormants}
    clicked={speakSegment} clickedDiphthong={speak}
    whatColor={(ipa) => whatColor(ipa, props.which, props.rhotics)}
  />;
}

// data on vowels & diphthongs for supported accents
const usVowels = ['ɑ', 'æ', 'ɛ', 'i', 'ɪ', 'ɔ', 'u', 'ʊ', 'ə', 'ɝ'];
const usDiphVowels = ['a', 'e', 'o'];
const usDiphthongs = ['aɪ̯', 'aʊ̯', 'eɪ̯', 'ɔɪ̯', 'oʊ̯'];
const usRhoticVowels = [''];
const usRhotics = ['ɑɹ', 'ɛɹ', 'ɪɹ', 'ɔɹ', 'ʊɹ', 'ɝ'];
const westVowels = ['ɑ', 'ɛ', 'i', 'ɪ', 'ʊ̞', 'ɐ', 'ɨ', 'ɝ'];
const westDiphVowels = ['a', 'e̞', 'o', 'ɔ', 'ə', 'ɤ̞', 'ʊ', 'ɯ', 'u'];
const westDiphthongs = ['aɪ̯', 'aʊ̯', 'e̞i̯', 'ɛə̯', 'ɔɪ̯', 'ɤ̞o̯', 'ɯu̯'];
const westRhoticVowels = ['e̞', 'o', 'ɤ'];
const westRhotics = ['ɑɹ', 'e̞ɹ', 'ɪɹ', 'oɹ', 'ɤɹ', 'ɝ'];
function getVowels(which: LanguageType) {
  return which === 'US' ? usVowels : westVowels;
}
/** additional vowels needed in order to draw diphthongs */
function getDiphVowels(which: LanguageType, rhotics?: boolean) {
  if (rhotics) { return which === 'US' ? usRhoticVowels : westRhoticVowels }
  return which === 'US' ? usDiphVowels : westDiphVowels;
}
function getDiphthongs(which: LanguageType, rhotics?: boolean) {
  if (rhotics) { return which === 'US' ? usRhotics : westRhotics }
  return which === 'US' ? usDiphthongs : westDiphthongs;
}
/** get list of diphthongs, or for rhotics, a lookup to associated formants */
function getDiphthongProp(which: LanguageType, rhotics?: boolean): string[] | Record<IpaType, number[]> {
  if (!rhotics) { return getDiphthongs(which); }
  if (which === 'US') {
    return {
      'ɑɹ': rhoticFormants['ɑɹ'],
      'ɛɹ': rhoticFormants['ɛɹ'],
      'ɪɹ': rhoticFormants['ɪɹ'],
      'ɔɹ': rhoticFormants['ɔɹ'],
      'ʊɹ': rhoticFormants['ʊɹ'],
    };
  } else {
    return {
      'ɑɹ': rhoticFormants['ɑɹ'],
      'e̞ɹ': rhoticFormants['e̞ɹ'],
      'ɪɹ': rhoticFormants['ɪɹ'],
      'oɹ': rhoticFormants['oɹ'],
      'ɤɹ': rhoticFormants['ɤɹ'],
    };
  }
}

/** get a lookup table of formants for the given language */
function getFormants(which: LanguageType, rhotics?: boolean) {
  let formants: Record<IpaType, number[]> = {};
  getVowels(which).forEach(v => formants[v] = allFormants[v]);
  formants['ɝ'] = rhoticFormants['ɝ'];
  getDiphVowels(which, rhotics).forEach(v => formants[v] = allFormants[v]);
  return formants;
}

/** get the color for the vowel & language */
function whatColor(ipa: IpaType, which: LanguageType, rhotics?: boolean): string {
  if (getVowels(which).includes(ipa)) {
    return 'black';
  } else if (getDiphVowels(which, rhotics).includes(ipa)) {
    return '#bbbbbb';
  } else if (getDiphthongs(which, rhotics).includes(ipa)) {
    return which === 'US' ? 'purple' : 'darkgreen';
  }
  return 'red';
}
